import { isBefore, addHours, parseISO } from 'date-fns';
import { authDelete, authGet } from '../../lib';
import {
  showError,
  getWarehouses,
  getTerminals,
  getAsset,
  getLocation,
  setLeadTimesSettingsCache,
  invalidateLeadTimesSettingsCache,
} from '../features';
import type { IdNamePair } from '../../types/globalTypes';
import type {
  WarehouseAppointmentSettingsAPI,
  WarehouseAssetType,
  WarehouseClosedDay,
  WarehouseDetailsAPI,
  WarehouseLeadTimesSettingsAPI,
  WarehouseLocationAPI,
  WarehouseLocationBasicDetails,
} from '../../types/warehouseTypes';
import type { NormalizeReturn, AppThunk } from '../types';
import { addResource, fetchResourceById, updateResource } from './resource';

/**
 * @function getBasicWarehouses
 * @description Fetches a list of basic warehouses.
 * @param {boolean} [ignoreCache=false] - Whether to ignore the cache and fetch new data. Defaults to false.
 * @param {boolean} [includeNotAssigned=false] - Whether to include warehouses that are not assigned to the current user.
 * Note that the results get cached only if `includeNotAssigned` is false which is the default.
 * @returns {Promise<NormalizeReturn<Array<IdNamePair>>>}
 */

export function getBasicWarehouses(
  ignoreCache: boolean = false,
  includeNotAssigned: boolean = false,
): AppThunk<Promise<NormalizeReturn<Array<IdNamePair>>>> {
  return async (dispatch, getState) => {
    const {
      warehouses: { basicWarehouses, lastFetchedWarehousesOn } = {},
      authPersist: { data: { userWarehouseIds } = {} } = {},
    } = getState();
    if (
      !ignoreCache &&
      basicWarehouses?.length &&
      !!lastFetchedWarehousesOn &&
      isBefore(new Date(), addHours(parseISO(lastFetchedWarehousesOn), 1))
    ) {
      return {
        data: basicWarehouses,
      };
    }
    const { data, error } = await authGet<Array<IdNamePair>>([
      '/warehouses/basic',
      {
        warehouseIDs:
          !userWarehouseIds?.length || includeNotAssigned
            ? undefined
            : userWarehouseIds,
      },
    ]);
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    // don't cache the results if includeNotAssigned is true
    if (!includeNotAssigned) {
      dispatch(getWarehouses(data));
    }
    return { data };
  };
}

export function getBasicTerminals(
  ignoreCache = false,
): AppThunk<Promise<NormalizeReturn<Array<IdNamePair>>>> {
  return async (dispatch, getState) => {
    const { warehouses: { basicTerminals, lastFetchedTerminalsOn } = {} } =
      getState();
    if (
      !ignoreCache &&
      basicTerminals?.length &&
      !!lastFetchedTerminalsOn &&
      isBefore(new Date(), addHours(parseISO(lastFetchedTerminalsOn), 1))
    ) {
      return {
        data: basicTerminals,
      };
    }
    const { data, error } = await authGet<Array<IdNamePair>>(
      '/system/terminals/basic',
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    dispatch(getTerminals(data));
    return { data };
  };
}

export function getWarehouseById(
  warehouseId: string | number,
): AppThunk<Promise<NormalizeReturn<WarehouseDetailsAPI>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<WarehouseDetailsAPI>(
      `/warehouses/${warehouseId}`,
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    return { data };
  };
}

export function getWarehouseLeadTimesSettings(
  warehouseId: string | number,
  ignoreCache: boolean = false,
): AppThunk<Promise<NormalizeReturn<WarehouseLeadTimesSettingsAPI>>> {
  return async (dispatch, getState) => {
    const cacheKey = String(warehouseId);
    const { warehouses: { leadTimesSettingsCache } = {} } = getState();
    const cached = leadTimesSettingsCache?.[cacheKey];

    if (
      !ignoreCache &&
      cached &&
      !!cached.fetchedOn &&
      isBefore(new Date(), addHours(parseISO(cached.fetchedOn), 1))
    ) {
      return { data: cached.data };
    }

    const { data, error } = await authGet<WarehouseLeadTimesSettingsAPI>(
      `/warehouses/${warehouseId}/lead-time-settings`,
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    if (data) {
      dispatch(setLeadTimesSettingsCache({ warehouseId: cacheKey, data }));
    }
    return { data };
  };
}

export function updateWarehouseLeadTimeSettings(
  warehouseId: string | number,
  payload: WarehouseLeadTimesSettingsAPI,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      updateResource<
        WarehouseLeadTimesSettingsAPI,
        WarehouseLeadTimesSettingsAPI
      >({
        baseUrl: `/warehouses/${warehouseId}/lead-time-settings`,
        payload,
        shouldSetUILoading: true,
      }),
    );
    if (error) {
      return { error };
    }
    dispatch(invalidateLeadTimesSettingsCache(warehouseId));
    return { data };
  };
}

export function getWarehouseClosedDays(
  warehouseId: number | string,
  month?: number,
  signal?: AbortSignal,
): AppThunk<Promise<NormalizeReturn<WarehouseClosedDay>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<WarehouseClosedDay>(
      [`/warehouses/${warehouseId}/closed-days`, { month }],
      undefined,
      undefined,
      { signal },
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    return { data };
  };
}

export function getWarehouseClosedDay(
  warehouseId: number | string,
  closedDayId: number | string,
): AppThunk<Promise<NormalizeReturn<WarehouseClosedDay>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<WarehouseClosedDay>(
      `/warehouses/${warehouseId}/closed-days/${closedDayId}`,
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    return { data };
  };
}

export function deleteWarehouseClosedDay(
  warehouseId: number | string,
  closedDayId: number | string,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    const { data, error } = await authDelete(
      `/warehouses/${warehouseId}/closed-days/${closedDayId}`,
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    return { data };
  };
}

export function upsertWarehouseClosedDay(
  warehouseId: number | string,
  payload: WarehouseClosedDay,
): AppThunk<Promise<NormalizeReturn<WarehouseClosedDay>>> {
  return async (dispatch) => {
    const { error, data } = await dispatch(
      addResource<WarehouseClosedDay>({
        baseUrl: `/warehouses/${warehouseId}/closed-days`,
        payload,
        shouldSetUILoading: false,
        shouldShowErrorMessage: true,
      }),
    );
    if (error) {
      return { error };
    }

    return { data };
  };
}

export function updateWarehouseAppointmentSettings(
  warehouseId: number | string,
  payload: WarehouseAppointmentSettingsAPI,
): AppThunk<Promise<NormalizeReturn<WarehouseAppointmentSettingsAPI>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<
        WarehouseAppointmentSettingsAPI,
        WarehouseAppointmentSettingsAPI
      >({
        baseUrl: `/warehouses/${warehouseId}/appointment-settings`,
        payload,
        shouldSetUILoading: true,
        message: 'Settings updated',
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function getWarehouseAppointmentSettings(
  warehouseId: number | string,
): AppThunk<Promise<NormalizeReturn<WarehouseAppointmentSettingsAPI>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<WarehouseAppointmentSettingsAPI>(
      `/warehouses/${warehouseId}/appointment-settings`,
    );
    if (error) {
      dispatch(showError({ message: error.message || 'An error occurred' }));
      return { error };
    }
    return { data };
  };
}

export function getBasicVehiclesByWarehouseId(
  warehouseIds: [],
): AppThunk<Promise<NormalizeReturn<Array<IdNamePair>>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<Array<IdNamePair>>([
      `/warehouses/assets/basic`,
      { warehouseIds, type: 1 },
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.Message || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getWarehouseLocationAsync(
  warehouseId: number | string,
  locationIdOrName: string | number,
): AppThunk<Promise<NormalizeReturn<WarehouseLocationAPI>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<WarehouseLocationAPI>(
      `/warehouses/${warehouseId}/locations/${locationIdOrName}`,
    );
    if (error) {
      dispatch(
        showError({
          message: error.message || error.Message || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getWarehouseAsset({
  warehouseId,
  assetIdOrRef,
  assetType,
  shouldSetUILoading = true,
  ignoreCache = false,
}: {
  warehouseId: number | string;
  assetIdOrRef: string;
  assetType: WarehouseAssetType;
  shouldSetUILoading?: boolean;
  ignoreCache?: boolean;
}): AppThunk<Promise<NormalizeReturn<IdNamePair>>> {
  return async (dispatch, getState) => {
    const {
      warehouses: {
        assets: { [assetIdOrRef]: asset } = {},
        lastFetchedAssetsOn,
      } = {},
    } = getState();
    if (
      !ignoreCache &&
      asset &&
      asset.assetType === assetType &&
      !!lastFetchedAssetsOn &&
      isBefore(new Date(), addHours(parseISO(lastFetchedAssetsOn), 1))
    ) {
      return { data: asset };
    }
    const { data, error } = await dispatch(
      fetchResourceById<IdNamePair>({
        baseUrl: '/warehouses',
        id: warehouseId,
        path: `assets/${assetIdOrRef}/basic-details`,
        query: { type: assetType },
        shouldSetUILoading,
      }),
    );
    if (error) {
      return { error };
    }
    if (data) {
      dispatch(getAsset({ input: assetIdOrRef, data: { ...data, assetType } }));
    }
    return { data };
  };
}

export function getWarehouseLocationBasicDetails({
  warehouseId,
  locationIdOrName,
  shouldSetUILoading = true,
  ignoreCache = false,
}: {
  warehouseId: number | string;
  locationIdOrName: string;
  shouldSetUILoading?: boolean;
  ignoreCache?: boolean;
}): AppThunk<Promise<NormalizeReturn<WarehouseLocationBasicDetails>>> {
  return async (dispatch, getState) => {
    // Create composite cache key to ensure uniqueness across warehouses
    const cacheKey = `${warehouseId}-${locationIdOrName}`;

    const {
      warehouses: {
        locations: { [cacheKey]: cachedLocation } = {},
        lastFetchedLocationsOn,
      } = {},
    } = getState();

    if (
      !ignoreCache &&
      cachedLocation &&
      !!lastFetchedLocationsOn &&
      isBefore(new Date(), addHours(parseISO(lastFetchedLocationsOn), 1))
    ) {
      return { data: cachedLocation };
    }

    const { data, error } = await dispatch(
      fetchResourceById<WarehouseLocationBasicDetails>({
        baseUrl: '/warehouses',
        id: warehouseId,
        path: `locations/${locationIdOrName}/basic-details`,
        shouldSetUILoading,
      }),
    );

    if (error) {
      return { error };
    }

    if (data) {
      // Cache the location data with composite key (warehouseId-locationIdOrName)
      dispatch(
        getLocation({
          cacheKey,
          data: { ...data, warehouseId },
        }),
      );
    }

    return { data };
  };
}
